import { useMutation } from "@tanstack/react-query";
import {
    ArrowLeftIcon,
    CheckCircleIcon,
    CheckIcon,
    EyeIcon,
    EyeSlashIcon,
    KeyIcon,
    LockClosedIcon,
    ShieldCheckIcon,
    XCircleIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { extractApiError } from "@/lib/api-error";

import { changeOwnPassword } from "../api/settingsApi";

type PasswordField = "currentPassword" | "newPassword" | "confirmPassword";

const fields: Array<{ key: PasswordField; label: string; placeholder: string }> = [
    {
        key: "currentPassword",
        label: "Current password",
        placeholder: "Enter your current password",
    },
    {
        key: "newPassword",
        label: "New password",
        placeholder: "Create a new password",
    },
    {
        key: "confirmPassword",
        label: "Confirm new password",
        placeholder: "Re-enter your new password",
    },
];

export const SettingsSecurityRoute = () => {
    const [showPasswords, setShowPasswords] = useState(false);
    const [flashMessage, setFlashMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
    const [formData, setFormData] = useState<Record<PasswordField, string>>({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });
    const [errors, setErrors] = useState<Partial<Record<PasswordField, string>>>({});

    useEffect(() => {
        if (!flashMessage) return;
        const timer = window.setTimeout(() => setFlashMessage(null), 5000);
        return () => window.clearTimeout(timer);
    }, [flashMessage]);

    const resetPasswordForm = () => {
        setFormData({ currentPassword: "", newPassword: "", confirmPassword: "" });
        setErrors({});
        setShowPasswords(false);
    };

    const passwordChecks = useMemo(
        () => [
            {
                label: "At least 8 characters",
                valid: formData.newPassword.length >= 8,
            },
            {
                label: "Different from your current password",
                valid: Boolean(formData.newPassword) && formData.newPassword !== formData.currentPassword,
            },
            {
                label: "Passwords match",
                valid: Boolean(formData.confirmPassword) && formData.newPassword === formData.confirmPassword,
            },
        ],
        [formData]
    );

    const changePasswordMutation = useMutation({
        mutationFn: () =>
            changeOwnPassword({
                currentPassword: formData.currentPassword,
                newPassword: formData.newPassword,
            }),
        onSuccess: (response) => {
            resetPasswordForm();
            setFlashMessage({
                type: "success",
                text: response.message || "Password changed successfully.",
            });
        },
        onError: (error) => {
            setFlashMessage({ type: "error", text: extractApiError(error) });
        },
    });

    const validatePasswordForm = () => {
        const nextErrors: Partial<Record<PasswordField, string>> = {};

        if (!formData.currentPassword) nextErrors.currentPassword = "Current password is required.";
        if (!formData.newPassword) {
            nextErrors.newPassword = "New password is required.";
        } else if (formData.newPassword.length < 8) {
            nextErrors.newPassword = "New password must be at least 8 characters.";
        } else if (formData.newPassword === formData.currentPassword) {
            nextErrors.newPassword = "New password must be different from the current password.";
        }
        if (!formData.confirmPassword) {
            nextErrors.confirmPassword = "Please confirm the new password.";
        } else if (formData.confirmPassword !== formData.newPassword) {
            nextErrors.confirmPassword = "Passwords do not match.";
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleChange = (key: PasswordField, value: string) => {
        setFormData((previous) => ({ ...previous, [key]: value }));
        if (errors[key]) setErrors((previous) => ({ ...previous, [key]: undefined }));
        if (flashMessage) setFlashMessage(null);
    };

    const handlePasswordSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (validatePasswordForm()) changePasswordMutation.mutate();
    };

    return (
        <div className="space-y-5">
            <Link
                to="/settings"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 transition hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            >
                <ArrowLeftIcon className="h-4 w-4" />
                Back to account
            </Link>

            {flashMessage ? (
                <div
                    className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm font-medium ${
                        flashMessage.type === "success"
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400"
                            : "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-400"
                    }`}
                >
                    {flashMessage.type === "success" ? (
                        <CheckCircleIcon className="h-5 w-5 shrink-0" />
                    ) : (
                        <XCircleIcon className="h-5 w-5 shrink-0" />
                    )}
                    <span>{flashMessage.text}</span>
                    <button
                        type="button"
                        onClick={() => setFlashMessage(null)}
                        className="ml-auto opacity-60 transition hover:opacity-100"
                        aria-label="Dismiss message"
                    >
                        <XCircleIcon className="h-4 w-4" />
                    </button>
                </div>
            ) : null}

            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="border-b border-slate-100 px-5 py-5 dark:border-slate-800 sm:px-6">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300">
                            <LockClosedIcon className="h-5 w-5" />
                        </div>
                        <div>
                            <h3 className="text-base font-semibold text-slate-900 dark:text-white">Change password</h3>
                            <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">Choose a strong password that you do not use elsewhere.</p>
                        </div>
                    </div>
                </div>

                <form onSubmit={handlePasswordSubmit} className="p-5 sm:p-6">
                    <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_260px]">
                        <div className="space-y-5">
                            {fields.map(({ key, label, placeholder }) => (
                                <div key={key}>
                                    <label htmlFor={key} className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">
                                        {label}
                                    </label>
                                    <div className="relative">
                                        <KeyIcon className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                                        <input
                                            id={key}
                                            type={showPasswords ? "text" : "password"}
                                            value={formData[key]}
                                            onChange={(event) => handleChange(key, event.target.value)}
                                            placeholder={placeholder}
                                            className={`w-full rounded-xl border bg-white px-10 py-3 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:outline-none focus:ring-4 dark:bg-slate-950 dark:text-slate-100 ${
                                                errors[key]
                                                    ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/15 dark:border-rose-700"
                                                    : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/15 dark:border-slate-700"
                                            }`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPasswords((previous) => !previous)}
                                            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600 dark:hover:text-slate-200"
                                            aria-label={showPasswords ? "Hide passwords" : "Show passwords"}
                                        >
                                            {showPasswords ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                        </button>
                                    </div>
                                    {errors[key] ? <p className="mt-1.5 text-xs text-rose-600 dark:text-rose-400">{errors[key]}</p> : null}
                                </div>
                            ))}
                        </div>

                        <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950/70">
                            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white">
                                <ShieldCheckIcon className="h-5 w-5 text-primary-500" />
                                Password checklist
                            </div>
                            <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                                Meet these requirements before saving your new password.
                            </p>
                            <div className="mt-4 space-y-3">
                                {passwordChecks.map(({ label, valid }) => (
                                    <div key={label} className="flex items-start gap-2.5 text-xs">
                                        <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${valid ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-400 dark:bg-slate-800"}`}>
                                            {valid ? <CheckIcon className="h-2.5 w-2.5" /> : null}
                                        </span>
                                        <span className={valid ? "text-emerald-700 dark:text-emerald-400" : "text-slate-500 dark:text-slate-400"}>
                                            {label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 flex flex-wrap justify-end gap-3 border-t border-slate-100 pt-5 dark:border-slate-800">
                        <Button type="button" variant="outline" onClick={resetPasswordForm} disabled={changePasswordMutation.isPending}>
                            Reset form
                        </Button>
                        <Button type="submit" disabled={changePasswordMutation.isPending} className="gap-2">
                            <KeyIcon className="h-4 w-4" />
                            {changePasswordMutation.isPending ? "Updating password..." : "Update password"}
                        </Button>
                    </div>
                </form>
            </section>
        </div>
    );
};
